import DateFnsUtils from '@date-io/date-fns';
import { Box, Button, Container, makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import { ChevronLeft, ChevronRight } from '@material-ui/icons';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import React from 'react';
import { useDatadogClickTracking } from '../../datadog';

const useStyles = makeStyles(theme => ({
    dayWrapper: {
        display: 'flex',
        flexDirection: 'row',
        alignContent: 'flex-start',
        justifyContent: 'center',
        textAlign: 'center',
        flexWrap: 'nowrap',
        minHeight: '150px',
        gap: '8px',
        padding: '0px',
    },
    chevron: {
        cursor: 'pointer',
        fontSize: '32px',
        '&:hover': {
            filter: 'brightness(0.7)',
        },
    },
    chevronDisabled: {
        cursor: 'not-allowed',
        fontSize: '32px',
        color: '#ccc',
    },
    day: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: '8px',
        minHeight: '15px',
        flex: '1 1 0',
        whiteSpace: 'nowrap',
        fontSize: '14px',
        [theme.breakpoints.down('sm')]: {
            fontSize: '13px',
        },
    },
    dayLabel: {
        color: '#444444',
        textAlign: 'center',
        whiteSpace: 'nowrap',
    },
    timeWrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        textAlign: 'center',
        width: '100%',
        minWidth: '85px',
        gap: '8px',
        maxHeight: '220px',
        overflowY: 'auto',
        [theme.breakpoints.down('xs')]: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '8px',
        },
    },
    time: {
        boxSizing: 'border-box',
        borderRadius: '3px',
        width: '100%',
        minHeight: '32px',
        maxHeight: '32px',
        overflow: 'hidden',
        border: `1px solid primary`,
        color: 'primary',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        [theme.breakpoints.down('sm')]: {
            fontSize: '12px',
        },
    },
    payCta: {
        cta: {
            width: '260px',
            height: '48px',
            borderRadius: '24px!important',
            fontSize: '18px',
            padding: theme.spacing(1 / 2),
        },
    },
}));

function convertStringToReadableDate(dateString) {
    const [year, month, day] = dateString.split('-');
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
    });
}

function formatTimeString(time, timezone) {
    return new Date(time)
        .toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: timezone,
        })
        .toLowerCase()
        .replace(' ', '');
}

export default function ClinicianTimeSelectionContainer({
    clinician,
    selectedClinician,
    availability,
    visitTime,
    onVisitTimeSelected,
    timezone,
}) {
    const classes = useStyles();

    const { trackClick } = useDatadogClickTracking('adjust-date-button', {
        page: 'book-with-clinician',
        feature: 'date-window-select',
    });

    const theme = useTheme();
    const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

    // Determine number of days to show based on screen width
    const numberOfDaysToShow = isMobile ? 1 : isNarrow ? 4 : 7;

    const [startDate, setStartDate] = React.useState(new Date());

    const handleNavigation = direction => {
        trackClick();
        const multiplier = direction === 'prev' ? -1 : 1;
        const daysToMove = numberOfDaysToShow === 7 ? 7 : numberOfDaysToShow === 4 ? 4 : 1;

        const newDate = new Date(startDate);
        newDate.setDate(newDate.getDate() + daysToMove * multiplier);
        setStartDate(newDate);
    };

    const handlePrev = () => {
        if (leftChevronDisabled) return;
        handleNavigation('prev');
    };
    const handleNext = () => handleNavigation('next');

    const handleDateChange = date => {
        trackClick();
        setStartDate(date);
    };

    const dates = React.useMemo(() => {
        return Array.from({ length: numberOfDaysToShow }, (_, index) => {
            const date = new Date(startDate);
            date.setDate(startDate.getDate() + index);
            return date.toISOString().split('T')[0];
        });
    }, [numberOfDaysToShow, startDate]);

    const leftChevronDisabled = React.useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Normalize to midnight
        const startDateMidnight = new Date(startDate);
        startDateMidnight.setHours(0, 0, 0, 0); // Normalize to midnight
        return (
            today >= startDateMidnight // Disable if startDate is today or in the past
        );
    }, [startDate]);

    if (!clinician || !availability) return null;

    return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Container className={classes.dayWrapper}>
                <ChevronLeft
                    className={leftChevronDisabled ? classes.chevronDisabled : classes.chevron}
                    onClick={handlePrev}
                    color='primary'
                />
                {dates.map((d, index) => (
                    <Box key={index} className={classes.day}>
                        <Box width={'100%'} className={classes.dayLabel}>
                            {numberOfDaysToShow > 1 ? (
                                convertStringToReadableDate(d)
                            ) : (
                                <DatePicker
                                    value={startDate}
                                    onChange={date => handleDateChange(date)}
                                    variant='dialog'
                                    className={classes.datePicker}
                                    inputProps={{
                                        style: { textAlign: 'center' },
                                    }}
                                />
                            )}
                        </Box>
                        <Box className={classes.timeWrapper}>
                            {availability[d] && availability[d].length ? (
                                availability[d].map(time => (
                                    <ClinicianTimeslot
                                        key={time}
                                        time={time}
                                        clinician={clinician}
                                        selectedClinician={selectedClinician}
                                        visitTime={visitTime}
                                        onVisitTimeSelected={onVisitTimeSelected}
                                        timezone={timezone}
                                    />
                                ))
                            ) : (
                                <Box color={'#666666'}>No times</Box>
                            )}
                        </Box>
                    </Box>
                ))}
                <ChevronRight className={classes.chevron} onClick={handleNext} color='primary' />
            </Container>
        </MuiPickersUtilsProvider>
    );
}

function ClinicianTimeslot({
    time,
    onVisitTimeSelected,
    clinician,
    selectedClinician,
    visitTime,
    timezone,
}) {
    const classes = useStyles();

    const selected = visitTime.has(time) && selectedClinician.uuid === clinician.uuid;
    return (
        <Button
            key={time}
            color='primary'
            variant={selected ? 'contained' : 'outlined'}
            className={classes.time}
            onClick={() => onVisitTimeSelected(time, clinician)}
        >
            {formatTimeString(time, timezone)}
        </Button>
    );
}
